import { normalizeForSearch } from '@narrativebench/editor-core';

export function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) {
    return n;
  }
  if (n === 0) {
    return m;
  }
  const row = new Array<number>(n + 1);
  for (let j = 0; j <= n; j++) {
    row[j] = j;
  }
  for (let i = 1; i <= m; i++) {
    let prev = row[0];
    row[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = row[j];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      row[j] = Math.min(row[j] + 1, row[j - 1] + 1, prev + cost);
      prev = tmp;
    }
  }
  return row[n];
}

export function levenshteinSimilarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) {
    return 1;
  }
  return 1 - levenshteinDistance(a, b) / maxLen;
}

function tagsAreSimilarPair(a: string, b: string, minSimilarity: number): boolean {
  const x = normalizeForSearch(a.trim());
  const y = normalizeForSearch(b.trim());
  if (x === y) {
    return true;
  }
  if (x.length < 2 || y.length < 2) {
    return false;
  }
  const sim = levenshteinSimilarity(x, y);
  const shortLen = Math.min(x.length, y.length);
  if (shortLen <= 2) {
    return sim >= 0.95;
  }
  if (shortLen <= 4) {
    return sim >= Math.min(0.92, minSimilarity + 0.08);
  }
  return sim >= minSimilarity;
}

export function computeSimilarTagGroups(
  tags: string[],
  options?: { minSimilarity?: number }
): string[][] {
  const minSimilarity = options?.minSimilarity ?? 0.74;
  const unique = [...new Set(tags.map((t) => t.trim()).filter(Boolean))];
  const n = unique.length;
  if (n < 2) {
    return [];
  }
  const parent = Array.from({ length: n }, (_, i) => i);
  const find = (i: number): number => {
    if (parent[i] !== i) {
      parent[i] = find(parent[i]);
    }
    return parent[i];
  };
  const union = (i: number, j: number): void => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) {
      parent[ri] = rj;
    }
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (tagsAreSimilarPair(unique[i], unique[j], minSimilarity)) {
        union(i, j);
      }
    }
  }
  const byRoot = new Map<number, string[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!byRoot.has(r)) {
      byRoot.set(r, []);
    }
    byRoot.get(r)!.push(unique[i]);
  }
  return Array.from(byRoot.values())
    .filter((g) => g.length >= 2)
    .map((g) => g.slice().sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })))
    .sort((a, b) => b.length - a.length);
}

export function groupSignature(group: string[]): string {
  return group
    .slice()
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .join('\u0001');
}
